import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Clock, Building2, Users, Shield, AlertCircle, ArrowLeft } from 'lucide-react';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(email, password);
      
      if (user.perfil === 'administrador_plataforma') {
        navigate('/plataforma');
      } else {
        navigate('/empresa');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary p-12 flex-col justify-between">
        <div>
          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 bg-accent rounded-xl flex items-center justify-center">
              <Clock className="w-7 h-7 text-white" />
            </div>
            <span className="text-3xl font-bold text-white font-['Outfit']">Jornyx</span>
          </div>
          
          <h1 className="text-4xl font-bold text-white mb-6 font-['Outfit'] leading-tight">
            Gestão de Jornada<br />Inteligente
          </h1>
          <p className="text-slate-300 text-lg max-w-md">
            Plataforma completa para controle de ponto, gestão de escalas, banco de horas e análise epidemiológica ocupacional.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-6">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
            <Building2 className="w-8 h-8 text-accent mb-3" />
            <h3 className="text-white font-semibold mb-1">Multi-empresa</h3>
            <p className="text-slate-400 text-sm">Gerencie múltiplas empresas em uma única plataforma</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
            <Users className="w-8 h-8 text-accent mb-3" />
            <h3 className="text-white font-semibold mb-1">Escalas Flexíveis</h3>
            <p className="text-slate-400 text-sm">5x2, 6x1, 12x36 e turnos rotativos</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
            <Shield className="w-8 h-8 text-accent mb-3" />
            <h3 className="text-white font-semibold mb-1">Portaria 671</h3>
            <p className="text-slate-400 text-sm">Conformidade total com a legislação brasileira</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
            <AlertCircle className="w-8 h-8 text-accent mb-3" />
            <h3 className="text-white font-semibold mb-1">Monitor de Riscos</h3>
            <p className="text-slate-400 text-sm">Alertas automáticos de passivo trabalhista</p>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex flex-col items-center justify-center p-8">
        {/* Back to Home Link */}
        <div className="w-full max-w-md mb-4">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-500 hover:text-primary transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Voltar ao início</span>
          </Link>
        </div>

        <Card className="w-full max-w-md border-slate-200 shadow-xl animate-slide-up">
          <CardHeader className="space-y-1 pb-6">
            <div className="flex items-center gap-2 lg:hidden mb-4">
              <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-primary font-['Outfit']">Jornyx</span>
            </div>
            <CardTitle className="text-2xl font-bold font-['Outfit']">Bem-vindo de volta</CardTitle>
            <CardDescription>Entre com suas credenciais para acessar</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="h-11"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Senha</Label>
                  <Link 
                    to="/recuperar-senha" 
                    className="text-sm text-accent hover:text-accent/80 transition-colors"
                  >
                    Esqueci minha senha
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                  className="h-11"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-11 bg-accent hover:bg-accent/90 text-white font-medium"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? 'Entrando...' : 'Entrar'}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm text-muted-foreground">
              <p className="font-medium text-slate-600 mb-2">Credenciais de demonstração:</p>
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500">Usuário Livre:</span>
                  <span className="font-mono bg-white px-2 py-1 rounded">usuariolivre@jornyx.com.br / livre1234</span>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t text-center">
              <p className="text-sm text-muted-foreground">
                Não tem uma conta?{' '}
                <Link to="/registro" className="text-accent hover:underline font-medium">
                  Solicitar acesso
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
